import React, { useEffect, useLayoutEffect, Suspense } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from 'next-themes';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/lib/queryClient';
import { persistOptions } from '@/lib/queryPersister';
import {
  registerOfflineMutationDefaults,
  resumePausedMutationsAfterRestore,
} from '@/lib/offlineMutations';
import { initOnlineManager } from '@/lib/onlineStatus';
import { OfflineBanner } from '@/components/OfflineBanner';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useNavigate } from 'react-router';
import { getFromStorage, saveToStorage } from './utils/storage';
import LangProvider from './providers/LanguageProvider';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { ServiceWorkerUpdateModal } from '@/components/ServiceWorkerUpdateModal';
import { logPWAStatus, setupOrientationHandler } from '@/utils/pwaUtils';
import { isElectron } from '@/utils/platformUtils';
import { registerSpaNavigator } from '@/utils/navigationUtils';
import { VideoLaunchSplash } from '@/components/launch/VideoLaunchSplash';
import Index from './pages/Index';
import ProtectedRoute from './components/ProtectedRoute';
import { OrganizationProvider } from '@/providers/OrganizationProvider';
import ClientsManagment from './components/ClientsManagment';
import { WhatsNewModal } from '@/components/WhatsNewModal';
import { useWhatsNew } from '@/hooks/useWhatsNew';
import { BoltLoader } from '@/components/ui/bolt-loader';
import { CapacitorUpdateModal } from '@/components/capacitor/CapacitorUpdateModal';
import ChunkErrorBoundary from '@/components/ChunkErrorBoundary';

// Lazy load all route components
const Auth = React.lazy(() => import('./pages/Auth'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const OrganizationSignup = React.lazy(() => import('./components/Signup'));
const TabManagement = React.lazy(() => import('@/components/TabManagement'));
const ProductManagement = React.lazy(() => import('@/components/ProductManagement'));
const ProviderManagement = React.lazy(() => import('@/components/ProviderManagement'));
const SalesManagement = React.lazy(() => import('@/components/SalesManagement'));
const CashBalancing = React.lazy(() => import('@/components/CashBalancing'));
const LogsManagement = React.lazy(() => import('@/components/LogsManagement'));
const SettingsManagement = React.lazy(() => import('@/components/SettingsManagement'));
const SubscriptionManagement = React.lazy(() => import('@/components/SubscriptionManagement'));
const KitchenView = React.lazy(() => import('@/components/kitchen/KitchenView'));
const ReservationsView = React.lazy(() => import('@/components/reservations/ReservationsView'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const FastSwitch = React.lazy(() => import('./pages/FastSwitch'));

// Register replayable mutationFns before PersistQueryClientProvider restores the
// persisted cache, so offline mutations queued before an app restart can resume.
// Runs here (module scope) instead of queryClient.ts to avoid an import cycle.
registerOfflineMutationDefaults(queryClient);

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <BoltLoader size="lg" className="mx-auto" />
      <p className="mt-4 text-muted-foreground">Loading...</p>
    </div>
  </div>
);

//HashRouter in Electron (for file:// protocol), BrowserRouter in web
const Router = isElectron() ? HashRouter : BrowserRouter;

/** Binds React Router `navigate` to `navigateTo()` for client-side redirects from stores (e.g. fast-switch). */
function SpaNavigateRegistration() {
  const navigate = useNavigate();
  useLayoutEffect(() => {
    registerSpaNavigator((to, opts) => {
      navigate(to, opts ?? { replace: true });
    });
    return () => registerSpaNavigator(null);
  }, [navigate]);
  return null;
}

const App = () => {
  const { showModal, changes, handleModalClose } = useWhatsNew();

  useEffect(() => {
    const teardownOnlineManager = initOnlineManager();

    const language: string = getFromStorage('language');
    if (!language || !['en', 'es'].includes(language)) {
      saveToStorage('language', 'en');
    }

    logPWAStatus();
    setupOrientationHandler();

    return () => {
      teardownOnlineManager();
    };
  }, []);

  return (
    <LangProvider>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={persistOptions}
        onSuccess={() => {
          void resumePausedMutationsAfterRestore(queryClient);
        }}
      >
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <TooltipProvider>
            <Router>
              <SpaNavigateRegistration />
              <VideoLaunchSplash>
                <ServiceWorkerUpdateModal />
                <CapacitorUpdateModal />
                <OfflineBanner />
                <Toaster />
                <Sonner />
                <PWAInstallPrompt />
                <WhatsNewModal
                  open={showModal}
                  onOpenChange={handleModalClose}
                  changes={changes}
                />
                <ChunkErrorBoundary>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/signup" element={<OrganizationSignup />} />
                      <Route path="/auth/reset-password" element={<ResetPassword />} />
                      <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                      <Route element={<OrganizationProvider><ProtectedRoute /></OrganizationProvider>}>
                        <Route element={<Index />}>
                          <Route path="/" element={<Navigate to="/tabs" replace />} />
                          <Route path="/fast-switch" element={<FastSwitch />} />
                          <Route path="/tabs" element={<TabManagement />} />
                          <Route path="/products" element={<ProductManagement />} />
                          <Route path="/providers" element={<ProviderManagement />} />
                          <Route path="/clients" element={<ClientsManagment />} />
                          <Route path="/sales" element={<SalesManagement />} />
                          <Route path="/cash" element={<CashBalancing />} />
                          <Route path="/logs" element={<LogsManagement />} />
                          <Route path="/reports" element={<Navigate to="/logs" replace />} />
                          <Route path="/settings" element={<SettingsManagement />} />
                          <Route path="/subscription" element={<SubscriptionManagement />} />
                          <Route path="/kitchen" element={<KitchenView />} />
                          <Route path="/reservations" element={<ReservationsView />} />
                        </Route>
                      </Route>
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </ChunkErrorBoundary>
              </VideoLaunchSplash>
            </Router>
          </TooltipProvider>
        </ThemeProvider>
      </PersistQueryClientProvider>
    </LangProvider>
  );
};

export default App;
